import { FlexibleConnectedPositionStrategyOrigin } from '@angular/cdk/overlay';
import { Injectable } from '@angular/core';
import { Commands, Downloads, Resources, Results, Style } from '@local/client-contracts';
import { isResourceDownload } from '@local/common';
import { isNativeWindow } from '@local/common-web';
import { getFileNameWithoutExt, getIconByExtension, isHttpOrHttps } from '@local/ts-infra';
import { PopupRef, PopupService } from '@local/ui-infra';
import { ContextMenuComponent, ContextMenuData, ContextMenuItem, ContextMenuService } from '@shared/components';
import { DragData, DragMetadata } from '@shared/directives/drag-and-drop.directive';
import { EventsService } from '@shared/services';
import { DownloadsService } from '@shared/services/downloads.service';
import { FlagsService } from '@shared/services/flags.service';
import { isDownloadUrlCommand, isOpenUrlCommand, join } from '@shared/utils';
import { Subscription, take } from 'rxjs';
import { CommandsService } from 'src/app/bar/services/commands/commands.service';
import { HubService } from 'src/app/bar/services/hub.service';
import {
  ResultAttachmentPopupComponent,
  ResultAttachmentPopupData,
} from '../components/result-attachment/result-attachment-popup/result-attachment-popup.component';

const actionToIcon: Record<string, string> = {
  open: 'icon-launch',
  'open-url': 'icon-network',
  'open-desktop': 'icon-cube',
  download: 'icon-download2',
};

@Injectable()
export class ResultAttachmentService {
  private readonly isNativeWindow = isNativeWindow();
  private popupRefAttachments: PopupRef<ResultAttachmentPopupComponent, ResultAttachmentPopupData>;

  contextMenuOpened = false;
  activeContextMenu: PopupRef<ContextMenuComponent, ContextMenuData>;

  constructor(
    private contextMenuService: ContextMenuService,
    private downloadsService: DownloadsService,
    private commandsService: CommandsService,
    private eventService: EventsService,
    private hubService: HubService,
    private flagsService: FlagsService,
    private popupService: PopupService
  ) {}

  async download(dragData: DragData): Promise<Commands.Command> {
    const downloadUrl: Commands.DownloadUrl | null = isDownloadUrlCommand(dragData.model?.onDrag) && dragData.model?.onDrag;
    if (!downloadUrl) {
      return dragData.model?.onClick;
    }

    if (!(await this.isDownloadEnabled(dragData))) {
      return;
    }

    this.sendDownloadEvents(dragData);
    if (!this.isNativeWindow) {
      const req: Downloads.ResourceDownloadRequest = {
        type: 'Resource',
        url: downloadUrl.url,
        resource: dragData.metadata?.resource,
        icon: dragData.metadata?.icon,
      };
      await this.downloadsService.resourceDownload(req);
    } else {
      const id: string = await this.downloadsService.getId(downloadUrl.url, dragData.metadata?.resource?.linkId);
      const opened = await this.openDownload(id);
      if (opened) return;
      this.openDownloadOnCompleted(id);
    }
  }

  openPopupAttachments(
    attachments: Results.Attachment[],
    dragMetadata: DragMetadata,
    downloadEnabled: boolean,
    positionOrigin: FlexibleConnectedPositionStrategyOrigin | 'center'
  ) {
    if (this.popupRefAttachments) {
      this.popupRefAttachments.destroy();
    }
    this.popupRefAttachments = this.popupService.open(positionOrigin, ResultAttachmentPopupComponent, {
      attachments,
      dragMetadata,
      downloadEnabled,
    });
    this.popupRefAttachments.destroy$.pipe(take(1)).subscribe(() => {
      this.popupRefAttachments.destroy();
      this.popupRefAttachments = null;
    });
  }

  async openContextMenu(
    event: { x: number; y: number },
    dragData: DragData,
    previewAttachment: boolean
  ): Promise<PopupRef<ContextMenuComponent, ContextMenuData>> {
    if (!dragData.model || !(dragData.model as Results.Tag).onDrag) return;
    const openAt: { x: number; y: number } = {
      x: event.x,
      y: previewAttachment ? (this.isNativeWindow ? event?.y - 120 : event?.y - 80) : event?.y,
    };
    const items = await this.buildContextMenuItems(dragData);
    this.activeContextMenu = this.contextMenuService.open(
      openAt,
      {
        items,
        onInvoke: (item: ContextMenuItem) => this.handleContextMenuCommand(item.command, dragData),
      },
      { position: 'below' }
    );
    return this.activeContextMenu;
  }

  getAttachmentIcon(extension: string): Style.Icon {
    const icon = getIconByExtension(extension);
    return { lightUrl: icon };
  }

  private async openDownload(id: string): Promise<boolean> {
    const download: Downloads.Download = this.downloadsService.all[id];
    const downloaded: boolean = download && download.status === 'completed';
    if (downloaded) {
      const command: Commands.OpenUrl = { type: 'open-url', url: join(download.path, download.name) };
      await this.commandsService.executeCommand(command, {});
      return true;
    }
    return false;
  }

  private async openDownloadOnCompleted(id: string): Promise<void> {
    await this.downloadsService.setVisibility(id, 'visible');
    const subscription: Subscription = this.downloadsService.downloadsChange.subscribe(async () => {
      const download: Downloads.Download = this.downloadsService.all[id];
      const downloaded: boolean = download?.status === 'completed';
      if (downloaded) {
        const command: Commands.OpenUrl = { type: 'open-url', url: join(download.path, download.name) };
        let resource: Resources.Resource;
        if (isResourceDownload(download)) {
          resource = download.resource;
        }
        await this.commandsService.executeCommand(command, {
          resource: { id: resource.id, kind: 'link-resource' },
          linkId: resource.linkId,
        });
        subscription.unsubscribe();
      }
    });
  }

  async handleContextMenuCommand(command, dragData: DragData): Promise<Commands.OpenUrl> {
    const { url } = command;
    const metadata: DragMetadata = dragData?.metadata;
    const downloadUrl = isDownloadUrlCommand(dragData.model?.onDrag) && dragData.model?.onDrag?.url;
    const id: string = await this.downloadsService.getId(downloadUrl, metadata?.resource?.linkId);
    const download: Downloads.Download = this.downloadsService.all[id];

    if (isDownloadUrlCommand(command)) {
      if (download) {
        if (download.visibility !== 'visible') this.downloadsService.setVisibility(download.id, 'visible');
      } else {
        const req: Downloads.ResourceDownloadRequest = {
          type: 'Resource',
          url,
          resource: dragData.metadata?.resource,
          icon: dragData.metadata?.icon,
          name: dragData.model.text,
        };
        await this.downloadsService.resourceDownload(req);
      }
      this.sendDownloadEvents(dragData);
    } else if (isOpenUrlCommand(command)) {
      const opened: boolean = await this.openDownload(id);
      if (opened) return;
      this.openDownloadOnCompleted(id);
      this.sentOpenEvent(dragData);
      return command;
    }
  }

  private async buildContextMenuItems(dragData: DragData): Promise<ContextMenuItem[]> {
    const items: ContextMenuItem[] = [];

    const attachment = dragData.model as Results.Attachment;
    const downloadUrl: Commands.DownloadUrl | null =
      attachment.onDrag && isDownloadUrlCommand(attachment.onDrag) && (await this.isDownloadEnabled(dragData)) ? attachment.onDrag : null;
    const openUrl: Commands.OpenUrl | null = this.isOpenEnabled(attachment);
    const metadata: DragMetadata = dragData?.metadata;
    let downloaded = false;
    let download: Downloads.Download;
    if (downloadUrl) {
      const id: string = await this.downloadsService.getId(downloadUrl.url, metadata?.resource?.linkId);
      download = this.downloadsService.all[id];
      downloaded = download?.status === 'completed';
    }

    if (openUrl && this.isNativeWindow) {
      const { url } = downloaded ? downloadUrl : openUrl;

      items.push({
        id: 'open',
        text: 'Open',
        data: { url },
        command: { type: 'open-url', url } as Commands.OpenUrl,
        icon: { type: 'font-icon', value: actionToIcon['open'] },
      });
    }

    if (downloadUrl && (!download || download.visibility !== 'visible')) {
      const url = downloadUrl.url;

      items.push({
        id: 'download',
        text: 'Download',
        data: { url },
        command: { type: 'download-url', url } as Commands.DownloadUrl,
        icon: { type: 'font-icon', value: actionToIcon['download'] },
      });
    }

    return items;
  }

  private eventResources(dragData: DragData) {
    const resource = dragData?.metadata?.resource;
    const info = dragData?.metadata?.eventInfo;
    return [{ id: resource?.id, appId: resource?.appId, type: resource?.type, position: info?.position, list: info?.list }];
  }

  private sendDownloadEvents(dragData: DragData) {
    this.eventService.event('results_tags.download', {
      resources: this.eventResources(dragData),
      location: { title: this.hubService.currentLocation },
      search: dragData?.metadata?.eventInfo?.search,
      target: 'mouse_click',
    });
    this.eventService.event('results_tags.action', {
      resources: this.eventResources(dragData),
      location: { title: this.hubService.currentLocation },
      search: dragData?.metadata?.eventInfo?.search,
      label: 'download',
    });
  }

  private sentOpenEvent(dragData: DragData) {
    this.eventService.event('results_tags.open', {
      resources: this.eventResources(dragData),
      location: { title: this.hubService.currentLocation },
      search: dragData?.metadata?.eventInfo?.search,
      target: 'mouse_click',
    });
  }

  isDownloadEnabled(dragData: DragData): Promise<boolean> {
    return this.flagsService.isEnabled('fileDownloads', dragData.metadata?.resource?.linkId);
  }

  isOpenEnabled(attachment: Results.Attachment): Commands.OpenUrl {
    const url: string = (attachment.onDrag as Commands.DownloadUrl)?.url;
    if (!attachment.onClick && url && isHttpOrHttps(url)) {
      return {
        type: 'open-url',
        url,
      };
    }
    return isOpenUrlCommand(attachment.onClick) ? <Commands.OpenUrl>attachment.onClick : null;
  }
}
